import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  getCommunications,
  deleteCommunication,
  updateCommTaskStatus,
  completeCommFollowUp,
  getStaff,
  getStudents,
  getCommunicationTypes,
} from "../lib/api";
import { useAuth } from "../App";
import CommunicationDialog from "../components/communications/CommunicationDialog";
import { TYPE_LABELS, DIRECTION_LABELS, VISIBILITY_LABELS } from "../components/communications/constants";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function daysFromToday(yyyyMmDd) {
  if (!yyyyMmDd) return null;
  const d = new Date(yyyyMmDd + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}

function DueBadge({ date, status }) {
  if (!date) return null;
  const diff = daysFromToday(date);
  if (status === "done") {
    return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Done</Badge>;
  }
  if (diff < 0) return <Badge className="bg-red-100 text-red-700 border-red-200">Overdue {Math.abs(diff)}d</Badge>;
  if (diff === 0) return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Due today</Badge>;
  if (diff <= 3) return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Due in {diff}d</Badge>;
  return <Badge variant="outline">Due {date}</Badge>;
}

export default function CommunicationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);
  const [customTypes, setCustomTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingComm, setEditingComm] = useState(null);
  const [activeTab, setActiveTab] = useState("tasks");

  // Filters
  const [search, setSearch] = useState(params.get("search") || "");
  const [typeFilter, setTypeFilter] = useState(params.get("type") || "all");
  const [directionFilter, setDirectionFilter] = useState(params.get("direction") || "all");
  const [visibilityFilter, setVisibilityFilter] = useState(params.get("visibility") || "any");
  const [assignedFilter, setAssignedFilter] = useState(params.get("mine") === "1");
  const [openTasksFilter, setOpenTasksFilter] = useState(params.get("open_tasks") === "1");
  const [followUpFilter, setFollowUpFilter] = useState(params.get("follow_up") === "1");
  const [contactFilter, setContactFilter] = useState(params.get("contact") || "");
  const [studentFilter, setStudentFilter] = useState(params.get("student") || "all");
  const [dateFrom, setDateFrom] = useState(params.get("from") || "");
  const [dateTo, setDateTo] = useState(params.get("to") || "");
  const [sortBy, setSortBy] = useState("occurred_at_desc");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [staffRes, studRes, typesRes] = await Promise.all([
        getStaff(),
        getStudents("current", true),
        getCommunicationTypes(),
      ]);
      setStaff([...(staffRes.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setStudents(
        studRes.data
          .filter((s) => s.status === "current")
          .sort((a, b) => (a.preferred_name || a.first_name || "").localeCompare(b.preferred_name || b.first_name || ""))
      );
      setCustomTypes(typesRes.data);
      await reload();
    } catch {
      toast.error("Failed to load communications");
    } finally {
      setLoading(false);
    }
  };

  const reload = async () => {
    const q = {};
    if (search) q.search = search;
    if (typeFilter !== "all") q.type = typeFilter;
    if (directionFilter !== "all") q.direction = directionFilter;
    if (visibilityFilter !== "any") q.visibility = visibilityFilter;
    if (assignedFilter) q.assigned_to_me = true;
    if (openTasksFilter) q.has_open_tasks = true;
    if (followUpFilter) q.needs_follow_up = true;
    if (contactFilter) q.contact_name = contactFilter;
    if (studentFilter !== "all") q.student_id = studentFilter;
    if (dateFrom) q.date_from = dateFrom;
    if (dateTo) q.date_to = dateTo;
    const res = await getCommunications(q);
    setItems(res.data);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Open the Log Communication/Task modal automatically when arriving via the sidebar
  // shortcut (/communications?new=1). The ?new flag is stripped so a refresh/back won't
  // re-open it; the user stays on this page after closing the modal.
  useEffect(() => {
    if (params.get("new") === "1") {
      setEditingComm(null);
      setDialogOpen(true);
      const np = new URLSearchParams(params);
      np.delete("new");
      setParams(np, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    reload().catch(() => {});
    const np = {};
    if (search) np.search = search;
    if (typeFilter !== "all") np.type = typeFilter;
    if (directionFilter !== "all") np.direction = directionFilter;
    if (visibilityFilter !== "any") np.visibility = visibilityFilter;
    if (assignedFilter) np.mine = "1";
    if (openTasksFilter) np.open_tasks = "1";
    if (followUpFilter) np.follow_up = "1";
    if (contactFilter) np.contact = contactFilter;
    if (studentFilter !== "all") np.student = studentFilter;
    if (dateFrom) np.from = dateFrom;
    if (dateTo) np.to = dateTo;
    setParams(np, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, typeFilter, directionFilter, visibilityFilter, assignedFilter, openTasksFilter, followUpFilter, contactFilter, studentFilter, dateFrom, dateTo]);

  const sortedItems = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => {
      if (sortBy === "occurred_at_desc") return (b.occurred_at || "").localeCompare(a.occurred_at || "");
      if (sortBy === "occurred_at_asc") return (a.occurred_at || "").localeCompare(b.occurred_at || "");
      if (sortBy === "contact_name") return (a.contact_name || "").localeCompare(b.contact_name || "");
      if (sortBy === "type") return (a.type || "").localeCompare(b.type || "");
      if (sortBy === "follow_up") {
        const ad = a.follow_up_date || "9999"; const bd = b.follow_up_date || "9999";
        return ad.localeCompare(bd);
      }
      return 0;
    });
    return arr;
  }, [items, sortBy]);

  // Categorize items into the three tabs.
  const categorized = useMemo(() => {
    const tasks = [];
    const comms = [];
    const simple = [];
    sortedItems.forEach((c) => {
      const isTaskKind = c.direction === "task_only" || c.direction === "reminder";
      const hasOpenTasks = (c.tasks || []).some((t) => t.status !== "done");
      const hasFollowUp = c.follow_up_date && !c.follow_up_completed;
      if (isTaskKind || (hasOpenTasks && !hasFollowUp)) {
        tasks.push(c);
      } else if (hasFollowUp || hasOpenTasks) {
        comms.push(c);
      } else {
        simple.push(c);
      }
    });
    return { tasks, comms, simple };
  }, [sortedItems]);

  const itemsForTab = categorized[activeTab === "tasks" ? "tasks" : activeTab === "communications" ? "comms" : "simple"];
  const openCreate = () => { setEditingComm(null); setDialogOpen(true); };
  const openEdit = (c) => { setEditingComm(c); setDialogOpen(true); };

  const handleDelete = async (c) => {
    if (!window.confirm("Delete this communication log?")) return;
    try {
      await deleteCommunication(c.id);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const handleTaskToggle = async (commId, task) => {
    const next = task.status === "done" ? "open" : "done";
    try {
      await updateCommTaskStatus(commId, task.id, next);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update task");
    }
  };

  const handleFollowUpDone = async (commId) => {
    try {
      await completeCommFollowUp(commId);
      toast.success("Follow-up marked complete");
      reload();
    } catch (e) {
      toast.error("Failed to update follow-up");
    }
  };

  const clearFilters = () => {
    setSearch(""); setTypeFilter("all"); setDirectionFilter("all"); setVisibilityFilter("any");
    setAssignedFilter(false); setOpenTasksFilter(false); setFollowUpFilter(false);
    setContactFilter(""); setStudentFilter("all"); setDateFrom(""); setDateTo("");
  };

  const typeOptions = [
    ...Object.entries(TYPE_LABELS).map(([k, v]) => ({ value: k, label: v })),
    ...customTypes.map((t) => ({ value: t.id, label: t.label })),
  ];

  const filterPills = [
    assignedFilter && "My Tasks",
    openTasksFilter && "Open Tasks",
    followUpFilter && "Needs Follow-up",
    contactFilter && `Contact: ${contactFilter}`,
    studentFilter !== "all" && `Student`,
    typeFilter !== "all" && `Type`,
    directionFilter !== "all" && DIRECTION_LABELS[directionFilter],
    visibilityFilter !== "any" && VISIBILITY_LABELS[visibilityFilter],
    dateFrom && `From ${dateFrom}`,
    dateTo && `To ${dateTo}`,
    search && `“${search}”`,
  ].filter(Boolean);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00305E]" /></div>;

  return (
    <div className="space-y-6" data-testid="communications-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#00305E]">Communication Logs</h1>
          <p className="text-muted-foreground">{items.length} entries visible to you</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={openCreate} variant="outline" className="border-[#00305E] text-[#00305E]" data-testid="add-comm-btn">
            + Log Communication
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border">
          <TabsTrigger value="tasks" data-testid="comm-tab-tasks">
            Tasks &amp; Reminders <Badge className="ml-2 bg-amber-100 text-amber-800">{categorized.tasks.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="communications" data-testid="comm-tab-comms">
            Communications <Badge className="ml-2 bg-blue-100 text-blue-800">{categorized.comms.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="simple_logs" data-testid="comm-tab-simple">
            Simple Logs <Badge className="ml-2 bg-gray-100 text-gray-700">{categorized.simple.length}</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4 mt-4">

      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sort & Filter</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input placeholder="Search subject, notes, contact..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="comm-search" />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="comm-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {typeOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={directionFilter} onValueChange={setDirectionFilter}>
              <SelectTrigger data-testid="comm-direction-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Direction</SelectItem>
                <SelectItem value="incoming">Incoming</SelectItem>
                <SelectItem value="outgoing">Outgoing</SelectItem>
                <SelectItem value="task_only">Task</SelectItem>
                <SelectItem value="reminder">Reminder</SelectItem>
              </SelectContent>
            </Select>
            <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
              <SelectTrigger data-testid="comm-visibility-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any Visibility</SelectItem>
                <SelectItem value="only_me">Only Me</SelectItem>
                <SelectItem value="private">Private (+ admins)</SelectItem>
                <SelectItem value="clinical">Clinical Only</SelectItem>
                <SelectItem value="all">All Staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input placeholder="Contact name" value={contactFilter} onChange={(e) => setContactFilter(e.target.value)} data-testid="comm-contact-filter" />
            <Select value={studentFilter} onValueChange={setStudentFilter}>
              <SelectTrigger data-testid="comm-student-filter"><SelectValue placeholder="Any student" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Student</SelectItem>
                {students.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{(s.preferred_name || s.first_name) + " " + s.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="comm-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="comm-date-to" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={assignedFilter} onChange={(e) => setAssignedFilter(e.target.checked)} data-testid="comm-assigned-filter" />
              Assigned to me
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={openTasksFilter} onChange={(e) => setOpenTasksFilter(e.target.checked)} data-testid="comm-open-tasks-filter" />
              Has open tasks
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={followUpFilter} onChange={(e) => setFollowUpFilter(e.target.checked)} data-testid="comm-follow-up-filter" />
              Needs follow-up
            </label>
            <div className="ml-auto flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-56" data-testid="comm-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="occurred_at_desc">Newest first</SelectItem>
                  <SelectItem value="occurred_at_asc">Oldest first</SelectItem>
                  <SelectItem value="contact_name">Contact name</SelectItem>
                  <SelectItem value="type">Type</SelectItem>
                  <SelectItem value="follow_up">Follow-up date</SelectItem>
                </SelectContent>
              </Select>
              {filterPills.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="comm-clear-filters">Clear filters</Button>
              )}
            </div>
          </div>
          {filterPills.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {filterPills.map((p) => <Badge key={`filter-${p}`} variant="secondary">{p}</Badge>)}
            </div>
          )}
        </CardContent>
      </Card>

      {itemsForTab.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          {activeTab === "tasks" ? "No open tasks or reminders tied to communications." :
           activeTab === "communications" ? "No communications with follow-ups in this view." :
           "No simple log entries yet."}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {itemsForTab.map((c) => (
            <CommunicationCard
              key={c.id}
              comm={c}
              currentUserId={user?.id}
              onEdit={() => openEdit(c)}
              onDelete={() => handleDelete(c)}
              onToggleTask={(t) => handleTaskToggle(c.id, t)}
              onCompleteFollowUp={() => handleFollowUpDone(c.id)}
              onNavigateStudent={(sid) => navigate(`/students/${sid}`)}
              typeLabel={typeOptions.find((o) => o.value === c.type)?.label || c.type}
            />
          ))}
        </div>
      )}

        </TabsContent>
      </Tabs>

      <CommunicationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editingComm}
        staff={staff}
        students={students}
        customTypes={customTypes}
        onSaved={() => { setDialogOpen(false); reload(); getCommunicationTypes().then((r) => setCustomTypes(r.data)).catch(() => {}); }}
      />
    </div>
  );
}

function CommunicationCard({ comm, currentUserId, onEdit, onDelete, onToggleTask, onCompleteFollowUp, onNavigateStudent, typeLabel }) {
  const canEdit = comm.created_by === currentUserId;
  const isTaskOnly = comm.direction === "task_only";
  const visBadge = {
    only_me: <Badge className="bg-slate-200 text-slate-800">Only Me</Badge>,
    private: <Badge className="bg-gray-200 text-gray-800">Private</Badge>,
    clinical: <Badge className="bg-purple-100 text-purple-800 border-purple-200">Clinical Only</Badge>,
    all: <Badge className="bg-blue-100 text-blue-800 border-blue-200">All Staff</Badge>,
  }[comm.visibility] || null;

  const directionBadge = isTaskOnly ? (
    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">★ Task</Badge>
  ) : (
    <Badge variant="outline" className={comm.direction === "incoming" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-sky-50 text-sky-700 border-sky-200"}>
      {comm.direction === "incoming" ? "↓ Incoming" : "↑ Outgoing"}
    </Badge>
  );

  const headline = isTaskOnly
    ? (comm.subject || "Task")
    : (comm.contact_name || "Unknown contact");

  return (
    <Card data-testid={`comm-card-${comm.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="capitalize">{typeLabel}</Badge>
              {directionBadge}
              {visBadge}
              {comm.student_id && (
                <button onClick={() => onNavigateStudent(comm.student_id)} className="text-sm text-blue-600 hover:underline" data-testid={`comm-student-link-${comm.id}`}>
                  👤 {comm.student_name}
                </button>
              )}
            </div>
            <CardTitle className="text-base">
              {headline}
              {!isTaskOnly && comm.contact_role && <span className="text-muted-foreground font-normal text-sm"> · {comm.contact_role}</span>}
            </CardTitle>
            <CardDescription>
              {formatDate(comm.occurred_at)} · by {comm.created_by_name || "—"}
            </CardDescription>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} disabled={!canEdit && comm.visibility !== "all"} data-testid={`comm-edit-${comm.id}`}>Edit</Button>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={onDelete} disabled={!canEdit} data-testid={`comm-delete-${comm.id}`}>Delete</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!isTaskOnly && comm.subject && <div className="text-sm font-semibold">{comm.subject}</div>}
        {comm.content && <div className="text-sm text-gray-700 whitespace-pre-wrap">{comm.content}</div>}
        {!isTaskOnly && (comm.contact_phone || comm.contact_email) && (
          <div className="text-xs text-muted-foreground">
            {comm.contact_phone && <span>📞 {comm.contact_phone}</span>}
            {comm.contact_phone && comm.contact_email && <span> · </span>}
            {comm.contact_email && <span>✉️ {comm.contact_email}</span>}
          </div>
        )}

        {comm.tasks && comm.tasks.length > 0 && (
          <div className="bg-gray-50 rounded-md p-3 space-y-2">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Tasks</div>
            {comm.tasks.map((t) => (
              <div key={t.id} className="flex items-start gap-2 text-sm" data-testid={`comm-task-${t.id}`}>
                <input
                  type="checkbox"
                  checked={t.status === "done"}
                  onChange={() => onToggleTask(t)}
                  className="mt-0.5"
                  data-testid={`comm-task-toggle-${t.id}`}
                />
                <div className="flex-1">
                  <div className={t.status === "done" ? "line-through text-muted-foreground" : ""}>
                    {t.description}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                    <span>→ {t.assignee_name || "Unassigned"}</span>
                    {t.due_date && <DueBadge date={t.due_date} status={t.status} />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {comm.follow_up_date && (
          <div className={`rounded-md p-3 border ${comm.follow_up_completed ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm">
                <span className="font-semibold">Follow-up:</span>{" "}
                <DueBadge date={comm.follow_up_date} status={comm.follow_up_completed ? "done" : "open"} />
                {comm.follow_up_notes && <div className="text-xs text-muted-foreground mt-1">{comm.follow_up_notes}</div>}
              </div>
              {!comm.follow_up_completed && (
                <Button size="sm" variant="outline" onClick={onCompleteFollowUp} data-testid={`comm-follow-up-done-${comm.id}`}>
                  Mark done
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
